import type { MapLibreMap } from 'maplibre-gl';
import {
  RAINVIEWER_MAX_ZOOM,
  type RadarFrame,
  type RadarTileOptions,
  radarTileUrl,
} from '@repo/shared';

/**
 * Imperative management of the radar overlay.
 *
 * Each mounted frame gets its own raster source and layer, all transparent
 * except the active one. Mounted-but-transparent layers still fetch their
 * tiles, so stepping between mounted frames is an opacity change rather than a
 * network round-trip.
 *
 * Only a *window* of frames around the active one is mounted at a time. Keeping
 * all ~13 frames mounted means every pan or zoom fires a tile request per frame
 * at once — well over a hundred in one burst, which trips RainViewer's rate
 * limit and gets a "Rate limit exceeded" placeholder painted over the map. A
 * sliding window bounds both memory and the request burst, and because playback
 * advances one frame at a time the window slides one frame at a time too, which
 * spreads fetches out naturally instead of needing a throttle.
 *
 * This lives outside React because MapLibre owns its own mutable scene graph —
 * the React tree only decides *which* frame is active.
 */

const SOURCE_PREFIX = 'radar-source-';
const LAYER_PREFIX = 'radar-layer-';

/**
 * How many frames to keep mounted ahead of and behind the active one. Ahead
 * matters most (that's where playback is going); one behind covers stepping
 * back. Seven layers total is smooth without bursting the tile budget.
 */
const WINDOW_AHEAD = 5;
const WINDOW_BEHIND = 1;

/**
 * Highest zoom to request radar tiles at — RainViewer's own ceiling, see
 * `RAINVIEWER_MAX_ZOOM`. Above it MapLibre overzooms the last real tile, which
 * is what we want anyway: the composite is a ~1km mosaic, so higher-zoom tiles
 * would carry no extra detail. Capping here also stops a zoom-in from
 * refetching every frame at every zoom step.
 */
const RADAR_MAX_ZOOM = RAINVIEWER_MAX_ZOOM;

/** Frames are identified by their valid time, which is unique within a feed. */
const sourceId = (time: number) => `${SOURCE_PREFIX}${time}`;
const layerId = (time: number) => `${LAYER_PREFIX}${time}`;

/**
 * Tracks which frame times are mounted per map, so syncing doesn't have to call
 * `map.getStyle()` — that serializes the entire style (50+ layers on a Mapbox
 * basemap) and this runs on every animation tick.
 */
const mountedTimes = new WeakMap<MapLibreMap, Set<number>>();

/**
 * The tile options the mounted layers were built with, so a change to them can
 * be detected and the layers rebuilt. See `syncRadarLayers`.
 */
const mountedOptions = new WeakMap<MapLibreMap, string>();

/** Identity of a tile-options set, for comparing against what's mounted. */
const tileOptionsKey = (options: RadarTileOptions): string =>
  `${options.tileSize}/${options.colorScheme}/${options.smooth}/${options.showSnow}`;

function mounted(map: MapLibreMap): Set<number> {
  let times = mountedTimes.get(map);
  if (!times) {
    times = new Set();
    mountedTimes.set(map, times);
  }
  return times;
}

/**
 * The frames that should be mounted for a given active index, wrapping at the
 * ends so the loop stays seamless as playback rolls from the newest frame back
 * to the oldest.
 */
export function selectFrameWindow(
  frames: readonly RadarFrame[],
  activeIndex: number,
  ahead: number = WINDOW_AHEAD,
  behind: number = WINDOW_BEHIND,
): RadarFrame[] {
  if (frames.length === 0) return [];
  if (frames.length <= ahead + behind + 1) return [...frames];

  const window: RadarFrame[] = [];
  for (let offset = -behind; offset <= ahead; offset++) {
    const index = (activeIndex + offset + frames.length) % frames.length;
    const frame = frames[index];
    // A wrapped window can revisit an index on very short feeds.
    if (frame && !window.includes(frame)) window.push(frame);
  }
  return window;
}

export interface RadarLayerConfig {
  /** The frames to keep mounted — normally the output of `selectFrameWindow`. */
  frames: readonly RadarFrame[];
  tileOptions: RadarTileOptions;
}

/**
 * Brings the map's radar layers in line with `frames`, adding what's new and
 * removing what's no longer wanted. Safe to call repeatedly; it diffs against
 * what's already mounted rather than tearing everything down.
 */
export function syncRadarLayers(map: MapLibreMap, config: RadarLayerConfig): void {
  const { frames, tileOptions } = config;

  // Tile options are baked into each source's URL template, and MapLibre gives
  // no way to rewrite one in place. A frame mounted under the old options would
  // therefore keep serving the old tiles indefinitely — changing the colour
  // scheme would silently do nothing until the frame list rolled over. Rebuild
  // instead, which is correct and rare: this only fires when the viewer changes
  // a setting, never during playback.
  const optionsKey = tileOptionsKey(tileOptions);
  if (mountedOptions.get(map) !== optionsKey) {
    removeRadarLayers(map);
    mountedOptions.set(map, optionsKey);
  }

  const times = mounted(map);
  const wanted = new Set(frames.map((frame) => frame.time));

  for (const time of times) {
    if (wanted.has(time)) continue;
    removeFrame(map, time);
    times.delete(time);
  }

  const beforeId = firstSymbolLayerId(map);

  for (const frame of frames) {
    if (times.has(frame.time)) continue;

    map.addSource(sourceId(frame.time), {
      type: 'raster',
      tiles: [radarTileUrl(frame, tileOptions)],
      tileSize: tileOptions.tileSize,
      minzoom: 0,
      maxzoom: RADAR_MAX_ZOOM,
      attribution: '<a href="https://www.rainviewer.com/" target="_blank">RainViewer</a>',
    });

    map.addLayer(
      {
        id: layerId(frame.time),
        type: 'raster',
        source: sourceId(frame.time),
        paint: {
          // Mounted transparent so its tiles prefetch without being seen.
          'raster-opacity': 0,
          // MapLibre's own cross-fade fights our frame stepping — disable it.
          'raster-fade-duration': 0,
        },
      },
      // Keep place labels legible on top of the precipitation.
      beforeId,
    );

    times.add(frame.time);
  }
}

/**
 * Shows exactly one frame, by valid time. Called on every animation tick, so it
 * only touches layers that are actually mounted.
 */
export function showRadarFrame(map: MapLibreMap, activeTime: number, opacity: number): void {
  for (const time of mounted(map)) {
    const id = layerId(time);
    if (!map.getLayer(id)) continue;
    map.setPaintProperty(id, 'raster-opacity', time === activeTime ? opacity : 0);
  }
}

/** Removes every radar layer and source, e.g. when the overlay is torn down. */
export function removeRadarLayers(map: MapLibreMap): void {
  const times = mounted(map);
  for (const time of times) removeFrame(map, time);
  times.clear();
}

function removeFrame(map: MapLibreMap, time: number): void {
  if (map.getLayer(layerId(time))) map.removeLayer(layerId(time));
  if (map.getSource(sourceId(time))) map.removeSource(sourceId(time));
}

/**
 * The id of the first symbol (label) layer, which is where the radar gets
 * inserted so labels draw above it. Undefined on styles with no labels, in
 * which case the radar simply goes on top.
 *
 * Shared with the coverage scrim, which needs the same insertion point.
 */
export function firstSymbolLayerId(map: MapLibreMap): string | undefined {
  return map.getStyle()?.layers?.find((layer) => layer.type === 'symbol')?.id;
}
